import { z } from "zod";

export namespace CommonModels {
  /**
   * OnboardingStepEnumSchema
   * @type { enum }
   */
  export const OnboardingStepEnumSchema = z.enum([
    "resume_upload",
    "resume_analysis",
    "resume_analysis_failed",
    "personal_info",
    "work_experience",
    "education",
    "skills",
    "certifications",
    "completed",
  ]);
  export type OnboardingStepEnum = z.infer<typeof OnboardingStepEnumSchema>;
  export const OnboardingStepEnum = OnboardingStepEnumSchema.enum;

  /**
   * ProfileStatusEnumSchema
   * @type { enum }
   */
  export const ProfileStatusEnumSchema = z.enum([
    "initialized",
    "onboarding",
    "queued",
    "processing",
    "processed",
    "processing_failed",
  ]);
  export type ProfileStatusEnum = z.infer<typeof ProfileStatusEnumSchema>;
  export const ProfileStatusEnum = ProfileStatusEnumSchema.enum;

  /**
   * WorkSettingEnumSchema
   * @type { enum }
   */
  export const WorkSettingEnumSchema = z.enum(["On-site", "Remote", "Hybrid", "Unknown"]);
  export type WorkSettingEnum = z.infer<typeof WorkSettingEnumSchema>;
  export const WorkSettingEnum = WorkSettingEnumSchema.enum;

  /**
   * IndustryEnumSchema
   * @type { enum }
   */
  export const IndustryEnumSchema = z.enum([
    "Corporate",
    "Startup",
    "Non-profit",
    "Government",
    "Academic",
    "Professional Services",
    "Other",
    "Unknown",
  ]);
  export type IndustryEnum = z.infer<typeof IndustryEnumSchema>;
  export const IndustryEnum = IndustryEnumSchema.enum;

  /**
   * RelationshipEnumSchema
   * @type { enum }
   * @description Relationship to the person being endorsed,E,x,a,m,p,l,e,:, ,`,C,o,l,l,e,a,g,u,e,`
   */
  export const RelationshipEnumSchema = z.enum([
    "Colleague",
    "Manager",
    "Direct Report",
    "Client",
    "Mentor",
    "Peer",
    "Vendor",
    "Other",
  ]);
  export type RelationshipEnum = z.infer<typeof RelationshipEnumSchema>;
  export const RelationshipEnum = RelationshipEnumSchema.enum;

  /**
   * EndorsementStatusEnumSchema
   * @type { enum }
   * @description Current status of the endorsement,E,x,a,m,p,l,e,:, ,`,A,P,P,R,O,V,E,D,`
   */
  export const EndorsementStatusEnumSchema = z.enum([
    "REQUESTED",
    "PENDING_APPROVAL",
    "APPROVED",
    "REJECTED",
    "IGNORED",
  ]);
  export type EndorsementStatusEnum = z.infer<typeof EndorsementStatusEnumSchema>;
  export const EndorsementStatusEnum = EndorsementStatusEnumSchema.enum;

  /**
   * WatchlistJobFitStatusEnumSchema
   * @type { enum }
   */
  export const WatchlistJobFitStatusEnumSchema = z.enum(["PENDING", "RUNNING", "COMPLETED", "FAILED"]);
  export type WatchlistJobFitStatusEnum = z.infer<typeof WatchlistJobFitStatusEnumSchema>;
  export const WatchlistJobFitStatusEnum = WatchlistJobFitStatusEnumSchema.enum;

  /**
   * LiveShowCategoryEnumSchema
   * @type { enum }
   */
  export const LiveShowCategoryEnumSchema = z.enum(["FREE", "PREMIUM"]);
  export type LiveShowCategoryEnum = z.infer<typeof LiveShowCategoryEnumSchema>;
  export const LiveShowCategoryEnum = LiveShowCategoryEnumSchema.enum;

  /**
   * LiveShowStatusEnumSchema
   * @type { enum }
   */
  export const LiveShowStatusEnumSchema = z.enum(["SCHEDULED", "LIVE", "ENDED", "CANCELLED"]);
  export type LiveShowStatusEnum = z.infer<typeof LiveShowStatusEnumSchema>;
  export const LiveShowStatusEnum = LiveShowStatusEnumSchema.enum;

  /**
   * ProfileAnalyticsStatusEnumSchema
   * @type { enum }
   */
  export const ProfileAnalyticsStatusEnumSchema = z.enum([
    "no_data",
    "enqueued",
    "processing",
    "processing_full",
    "completed",
    "failed",
  ]);
  export type ProfileAnalyticsStatusEnum = z.infer<typeof ProfileAnalyticsStatusEnumSchema>;
  export const ProfileAnalyticsStatusEnum = ProfileAnalyticsStatusEnumSchema.enum;

  /**
   * PriorityEnumSchema
   * @type { enum }
   */
  export const PriorityEnumSchema = z.enum(["low", "medium", "high", "critical"]);
  export type PriorityEnum = z.infer<typeof PriorityEnumSchema>;
  export const PriorityEnum = PriorityEnumSchema.enum;

  /**
   * MediaUploadInstructionsSchema
   * @type { object }
   * @property { string } id Media ID
   * @property { string } method Method to upload with - PUT, POST, or custom instructions
   * @property { string } url Url to upload to
   * @property { array[] } fields Additional fields for the POST upload. Example: `key1,value1,key2,value2`
   * @property { MediaUploadInstructions } thumbnail Optional thumbnail upload instructions (for images with cropped thumbnails)
   */
  export const MediaUploadInstructionsSchema: z.ZodType<unknown> = // TODO: Fix type inference
    z.lazy(() =>
      z
        .object({
          id: z.string(),
          method: z.string(),
          url: z.string(),
          fields: z.array(z.array(z.string())),
          thumbnail: CommonModels.MediaUploadInstructionsSchema,
        })
        .partial(),
    );
  export type MediaUploadInstructions = z.infer<typeof MediaUploadInstructionsSchema>;

  /**
   * BizUserDtoSchema
   * @type { object }
   * @property { string } id
   * @property { string } email
   * @property { string } firstName
   * @property { string } lastName
   * @property { string } jobTitle
   * @property { string } city
   * @property { string } state
   * @property { string } profilePictureId
   * @property { string } profilePictureUrl
   */
  export const BizUserDtoSchema = z.object({
    id: z.string(),
    email: z.string(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    jobTitle: z.string().nullable(),
    city: z.string().nullable(),
    state: z.string().nullable(),
    profilePictureId: z.string().nullable(),
    profilePictureUrl: z.string().nullable(),
  });
  export type BizUserDto = z.infer<typeof BizUserDtoSchema>;

  /**
   * BizTagDtoSchema
   * @type { object }
   * @property { string } id
   * @property { string } name
   * @property { number } profileCount
   */
  export const BizTagDtoSchema = z.object({ id: z.string(), name: z.string(), profileCount: z.number() });
  export type BizTagDto = z.infer<typeof BizTagDtoSchema>;

  /**
   * MediaUploadFileRequestSchema
   * @type { object }
   * @property { string } fileName Original filename. Example: `profile.png`
   * @property { number } fileSize Original file size in bytes. Minimum: `1`. Example: `1024`
   * @property { string } mimeType File mime type. Example: `image/png`
   * @property { string } thumbnailFileName Thumbnail filename. Example: `profile-thumb.png`
   * @property { number } thumbnailFileSize Thumbnail file size in bytes. Minimum: `1`. Example: `512`
   * @property { string } thumbnailMimeType Thumbnail mime type (defaults to mimeType). Example: `image/png`
   */
  export const MediaUploadFileRequestSchema = z.object({
    fileName: z.string(),
    fileSize: z.number().gte(1),
    mimeType: z.string().optional(),
    thumbnailFileName: z.string().optional(),
    thumbnailFileSize: z.number().gte(1).optional(),
    thumbnailMimeType: z.string().optional(),
  });
  export type MediaUploadFileRequest = z.infer<typeof MediaUploadFileRequestSchema>;

  /**
   * PaginationDtoSchema
   * @type { object }
   * @property { string[] } items Items
   * @property { object[] } order List of order definitions
   * @property { number } page 1-indexed page number to begin from
   * @property { string } cursor ID of item to start after
   * @property { string } nextCursor Cursor for next set of items
   * @property { number } limit Items per response
   * @property { number } totalItems Total available items
   */
  export const PaginationDtoSchema = z.object({
    items: z.array(z.string()),
    order: z.array(z.object({})).optional(),
    page: z.number().optional(),
    cursor: z.string().optional(),
    nextCursor: z.string().optional(),
    limit: z.number(),
    totalItems: z.number(),
  });
  export type PaginationDto = z.infer<typeof PaginationDtoSchema>;
}
